<template>
  <div class="min-h-screen hs-page-backdrop">
    <div class="max-w-3xl mx-auto p-6 space-y-6">
      <!-- Warning Banner -->
      <div
        class="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-4"
      >
        <div class="flex items-start gap-3">
          <svg
            class="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
          <div>
            <p class="text-sm font-medium text-amber-800 dark:text-amber-300">
              Staging Environment Only
            </p>
            <p class="text-sm text-amber-700 dark:text-amber-400 mt-1">
              This will pull the latest code, run database migrations, and
              restart the staging server. The server will be briefly unavailable
              during restart.
            </p>
          </div>
        </div>
      </div>

      <!-- Deploy Card -->
      <div
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm"
      >
        <div class="p-6">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            Deploy to Staging
          </h2>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Runs:
            <code
              class="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded"
              >git pull</code
            >
            &rarr;
            <code
              class="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded"
              >run_migrations.sh stage</code
            >
            &rarr;
            <code
              class="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded"
              >pm2 restart all</code
            >
          </p>

          <div
            class="rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700 p-4 mb-6"
          >
            <p class="text-sm font-medium text-blue-800 dark:text-blue-300">
              Deploy verification nuance
            </p>
            <p class="text-sm text-blue-700 dark:text-blue-400 mt-1">
              A successful PM2 restart only means the processes restarted. This
              page also checks the staging root endpoint before and after deploy
              and compares <code>last_commit</code> so you can see whether new
              code is actually live.
            </p>
          </div>

          <!-- Confirm checkbox -->
          <label
            class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-6 select-none cursor-pointer"
          >
            <input
              v-model="confirmed"
              type="checkbox"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              :disabled="deploying"
            />
            I understand this will restart the staging server
          </label>

          <hs-button
            variant="primary"
            icon="send"
            :loading="deploying"
            :disabled="deploying || !confirmed"
            @click="runDeploy"
          >
            {{ deploying ? "Deploying..." : "Deploy Now" }}
          </hs-button>
        </div>
      </div>

      <!-- Results -->
      <div
        v-if="result"
        class="bg-white dark:bg-gray-800 rounded-lg border shadow-sm"
        :class="
          result.success
            ? 'border-green-300 dark:border-green-700'
            : 'border-red-300 dark:border-red-700'
        "
      >
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center gap-2">
            <span
              class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
              :class="
                result.success
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
              "
            >
              {{ result.success ? "SUCCESS" : "FAILED" }}
            </span>
            <span
              v-if="result.failed_step"
              class="text-sm text-red-600 dark:text-red-400"
            >
              Failed at: {{ result.failed_step }}
            </span>
          </div>
        </div>

        <div
          v-if="
            verification.before ||
            verification.after ||
            verification.message ||
            verification.beforeError ||
            verification.afterError
          "
          class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30"
        >
          <div class="flex items-center gap-2 mb-2">
            <span class="text-sm font-medium text-gray-900 dark:text-white">
              Live Verification
            </span>
            <span
              v-if="verification.message"
              class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
              :class="verificationToneClasses"
            >
              {{ verification.tone.toUpperCase() }}
            </span>
          </div>

          <p class="text-xs text-gray-600 dark:text-gray-400">
            The dashboard queries the staging root endpoint before and after the
            deploy request. If <code>last_commit</code> does not change, a PM2
            restart may have succeeded while the server is still running the
            same code.
          </p>

          <p
            v-if="verification.message"
            class="mt-3 text-sm"
            :class="verificationTextClasses"
          >
            {{ verification.message }}
          </p>

          <p
            v-if="verification.beforeError"
            class="mt-2 text-xs text-amber-700 dark:text-amber-400"
          >
            {{ verification.beforeError }}
          </p>

          <p
            v-if="verification.afterError"
            class="mt-2 text-xs text-red-700 dark:text-red-400"
          >
            {{ verification.afterError }}
          </p>

          <div class="grid md:grid-cols-2 gap-4 mt-4">
            <div v-if="verification.before">
              <p
                class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1"
              >
                Before
              </p>
              <pre
                class="text-xs bg-white dark:bg-gray-900 rounded p-3 overflow-x-auto text-gray-800 dark:text-gray-200"
                >{{ formatSnapshot(verification.before) }}</pre
              >
            </div>

            <div v-if="verification.after">
              <p
                class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1"
              >
                After
              </p>
              <pre
                class="text-xs bg-white dark:bg-gray-900 rounded p-3 overflow-x-auto text-gray-800 dark:text-gray-200"
                >{{ formatSnapshot(verification.after) }}</pre
              >
            </div>
          </div>
        </div>

        <!-- Step logs -->
        <div class="divide-y divide-gray-200 dark:divide-gray-700">
          <div v-for="(step, idx) in result.steps" :key="idx" class="p-4">
            <div class="flex items-center gap-2 mb-2">
              <span
                class="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold"
                :class="
                  stepSucceeded(step)
                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                "
              >
                {{ stepSucceeded(step) ? "✓" : "✗" }}
              </span>
              <span class="text-sm font-medium text-gray-900 dark:text-white">
                {{ step.label }}
              </span>
              <code class="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                exit {{ step.exit_code }}
              </code>
            </div>

            <div
              v-if="step.warning"
              class="mb-2 rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-3"
            >
              <p class="text-xs font-medium text-amber-800 dark:text-amber-300">
                Warning
              </p>
              <p class="text-xs text-amber-700 dark:text-amber-400 mt-1">
                {{ step.warning }}
              </p>
            </div>

            <div v-if="step.stdout" class="mb-2">
              <p
                class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1"
              >
                stdout
              </p>
              <pre
                class="text-xs bg-gray-50 dark:bg-gray-900 rounded p-3 overflow-x-auto text-gray-800 dark:text-gray-200 max-h-48 overflow-y-auto"
                >{{ step.stdout }}</pre
              >
            </div>

            <div v-if="step.stderr">
              <p
                class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1"
              >
                stderr
              </p>
              <pre
                class="text-xs bg-red-50 dark:bg-red-900/20 rounded p-3 overflow-x-auto text-red-800 dark:text-red-300 max-h-48 overflow-y-auto"
                >{{ step.stderr }}</pre
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Error message -->
      <div
        v-if="errorMessage"
        class="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 p-4"
      >
        <p class="text-sm text-red-800 dark:text-red-300">
          {{ errorMessage }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/helper/http";
import api from "@/helper/api";
import { HsButton } from "@/components/ui";

export default {
  name: "AdminDeployView",
  components: {
    HsButton,
  },
  props: {
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      confirmed: false,
      deploying: false,
      result: null,
      errorMessage: null,
      verification: {
        before: null,
        after: null,
        message: null,
        tone: "info",
        beforeError: null,
        afterError: null,
      },
    };
  },
  computed: {
    verificationToneClasses() {
      if (this.verification.tone === "success") {
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      }
      if (this.verification.tone === "warning") {
        return "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300";
      }
      if (this.verification.tone === "error") {
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      }
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    },
    verificationTextClasses() {
      if (this.verification.tone === "success") {
        return "text-green-700 dark:text-green-300";
      }
      if (this.verification.tone === "warning") {
        return "text-amber-700 dark:text-amber-300";
      }
      if (this.verification.tone === "error") {
        return "text-red-700 dark:text-red-300";
      }
      return "text-blue-700 dark:text-blue-300";
    },
  },
  methods: {
    resetVerification() {
      this.verification = {
        before: null,
        after: null,
        message: null,
        tone: "info",
        beforeError: null,
        afterError: null,
      };
    },
    stepSucceeded(step) {
      if (typeof step?.success === "boolean") {
        return step.success;
      }
      return step?.exit_code === 0;
    },
    formatSnapshot(snapshot) {
      return JSON.stringify(snapshot, null, 2);
    },
    getStagingInfoUrl() {
      if (!process.env.VUE_APP_BACKEND_API) {
        return null;
      }

      try {
        return new URL("/", process.env.VUE_APP_BACKEND_API).toString();
      } catch (error) {
        return null;
      }
    },
    async fetchStagingInfo() {
      const url = this.getStagingInfoUrl();
      if (!url) {
        throw new Error("Backend URL is not configured for verification.");
      }

      const response = await fetch(`${url}?_=${Date.now()}`, {
        cache: "no-store",
        credentials: "omit",
      });

      if (!response.ok) {
        throw new Error(
          `Verification request failed with status ${response.status}.`,
        );
      }

      return response.json();
    },
    async fetchStagingInfoWithRetry(previousCommit = null) {
      let lastSnapshot = null;
      let lastError = null;

      for (let attempt = 0; attempt < 6; attempt += 1) {
        try {
          const snapshot = await this.fetchStagingInfo();
          lastSnapshot = snapshot;
          if (!previousCommit || snapshot.last_commit !== previousCommit) {
            return snapshot;
          }
        } catch (error) {
          lastError = error;
        }

        if (attempt < 5) {
          await this.sleep(2000);
        }
      }

      if (lastSnapshot) {
        return lastSnapshot;
      }

      throw (
        lastError ||
        new Error("Could not verify the staging server after deploy.")
      );
    },
    updateVerificationSummary() {
      const before = this.verification.before;
      const after = this.verification.after;

      if (before && after) {
        if (before.last_commit !== after.last_commit) {
          this.verification.message = `Live commit changed from ${before.last_commit} to ${after.last_commit}.`;
          this.verification.tone = "success";
          return;
        }

        this.verification.message =
          "Staging is reachable after deploy, but last_commit did not change. PM2 restart may have succeeded while git pull did not move HEAD, so the server can still be running the old code.";
        this.verification.tone = "warning";
        return;
      }

      if (!before && after) {
        this.verification.message = `Staging responded after deploy with commit ${after.last_commit}, but the dashboard could not capture the pre-deploy commit.`;
        this.verification.tone = "warning";
        return;
      }

      if (this.verification.afterError) {
        this.verification.message =
          "The deploy request completed, but the dashboard could not verify the live staging server afterward.";
        this.verification.tone = "error";
      }
    },
    sleep(ms) {
      return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
      });
    },
    async runDeploy() {
      this.deploying = true;
      this.result = null;
      this.errorMessage = null;
      this.resetVerification();

      try {
        this.verification.before = await this.fetchStagingInfo();
      } catch (error) {
        this.verification.beforeError =
          error.message ||
          "Could not read the current staging commit before deploy.";
      }

      try {
        const response = await http.post(api.admin_deploy_staging);
        this.result = response.data;
      } catch (err) {
        if (err.response && err.response.data) {
          this.result = err.response.data;
        } else {
          this.errorMessage =
            err.message || "Network error — could not reach the server.";
        }
      } finally {
        try {
          this.verification.after = await this.fetchStagingInfoWithRetry(
            this.verification.before?.last_commit || null,
          );
        } catch (error) {
          this.verification.afterError =
            error.message ||
            "Could not verify the staging server after deploy.";
        }

        this.updateVerificationSummary();
        this.deploying = false;
        this.confirmed = false;
      }
    },
  },
};
</script>
